import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
    Add as AddIcon
} from '@mui/icons-material';

import {
    BeneficiaryModal,
    RemoveBeneficiaryModal,
    Text,
    Button,
    Container,
    ContentSmall,
    Title,
    Theme,
    i18n,
    tokens,
    ChangeRequestModal
} from '@simplybenefits/sb-ui-library';

import PersonBlock from '../PersonBlock.js';
import { addBeneficiary, editBeneficiary, removeBeneficiary } from '../../../actions/user.js';
import { createChangeRequest, submitChangeRequest, deleteChangeRequest } from '../../../actions/changeRequests.js';
import { createFolder, downloadFile, listFiles, removeFile, uploadFile } from '../../../actions/files.js';

/**
 * 
 * @returns 
 */
const Beneficiaries = ({
    loader,
    primaryBeneficiaries,
    contingentBeneficiaries,
    whitelabel,
    trustee,
    meta,
    dispatch
}) => {
    const [ beneficiary, setBeneficiary ] = useState();
    const [ isEdit, setIsEdit ] = useState(false);
    const [ isContingent, setIsContingent ] = useState(false);
    const [ beneficiaryModalOpen, setBeneficiaryModalOpen ] = useState(false);
    const [ removeBeneficiaryModalOpen, setRemoveBeneficiaryModalOpen ] = useState(false);

    const [ changeRequestModalOpen, setChangeRequestModalOpen ] = useState(false);
    const [ changeRequestData, setChangeRequestData ] = useState(null);

    /**
     * Close all modals and reset state variables related to beneficiary actions.
     */
    const onClose = () => {
        setBeneficiaryModalOpen(false);
        setRemoveBeneficiaryModalOpen(false);
        setBeneficiary(null);
        setIsEdit(false);
    };

    /**
     * Open the modal to add a new primary beneficiary.
     */
    const addPrimaryBeneficiary = () => {
        setBeneficiary(null);
        setIsEdit(false);
        setIsContingent(false);
        setBeneficiaryModalOpen(true);
    };

    /**
     * Open the modal to add a new contingent beneficiary.
     */
    const addContingentBeneficiary = () => {
        setBeneficiary(null);
        setIsEdit(false);
        setIsContingent(true);
        setBeneficiaryModalOpen(true);
    };

    /**
     * Open the modal to edit an existing beneficiary.
     * 
     * @param {Object} beneficiaryData - Data of the beneficiary to edit
     * @param {boolean} isContingentBeneficiary - Flag to indicate if the beneficiary is contingent
     */
    const showEditBeneficiary = (beneficiaryData, isContingentBeneficiary = false) => {
        setBeneficiary(beneficiaryData);
        setIsEdit(true);
        
        isContingentBeneficiary ?
            setIsContingent(true)
            : 
            setIsContingent(false);

        setBeneficiaryModalOpen(true);
    };

    /**
     * Open the modal to remove an existing beneficiary.
     * 
     * @param {Object} beneficiaryData - Data of the beneficiary to remove
     * @param {boolean} isContingentBeneficiary - Flag to indicate if the beneficiary is contingent
     */
    const showRemoveBeneficiary = (beneficiaryData, isContingentBeneficiary = false) => {
        setBeneficiary(beneficiaryData);

        isContingentBeneficiary ?
            setIsContingent(true)
            : 
            setIsContingent(false);

        setRemoveBeneficiaryModalOpen(true);
    };

    /**
     * Save the beneficiary data and close the modal.
     * 
     * @param {Object} beneficiaryData - Data of the beneficiary to save
     */
    const saveBeneficiary = (beneficiaryData) => {
        setBeneficiaryModalOpen(false);
        setBeneficiary(beneficiaryData);
    };

    /**
     * Create and open a change request form.
     * 
     * @param {Object} beneficiaryData - Data for creating the change request
     * @returns {Promise<boolean>}
     */
    const openChangeRequestModal = (beneficiaryData) => {
        setRemoveBeneficiaryModalOpen(false);
        setBeneficiaryModalOpen(false);
        setChangeRequestModalOpen(true);
        setChangeRequestData(beneficiaryData);
    };

    /**
     * Close the change request modal.
     */
    const closeChangeRequestModal = () => {
        setBeneficiaryModalOpen(false);
        setIsEdit(false);
        setChangeRequestModalOpen(false);
    };

    const styles = {
        titleBlock: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 10
        },
        iconStyle: {
            padding: "12px",
            color: "#414042",
            '&.MuiButtonBase-root:hover': {
                backgroundColor: Theme.colors.hoverGrey
            }
        }
    };

    return (
        <Container>
            <ContentSmall style={{ width: "60%" }}>
                <Title title={ i18n.t(tokens.titles.beneficiaries) } />

                <div>
                    <div id="beneficiaries" style={ styles.titleBlock }>
                        <Text mediumLarge>{ i18n.t(tokens.titles["primary beneficiaries"])}</Text>

                        <Button
                            testid="add-beneficiaries"
                            onClick={ () => addPrimaryBeneficiary(true) }
                            transparent
                            customStyle={{ marginLeft: 'auto' }}
                        >
                            <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                            { i18n.t(tokens.buttons["add beneficiary"]) }
                        </Button>
                    </div>

                    {
                        primaryBeneficiaries.length === 0 ?
                            <Text testid="beneficiaries" light medium>{ i18n.t(tokens.labels.beneficiaries.youHaveNotDefinedAnyPrimaryBeneficiaries) }</Text>
                            :
                            primaryBeneficiaries.map((beneficiaryData, i) => (
                                <PersonBlock
                                    testid={ i === 0 ? "primary" : undefined }
                                    key={beneficiaryData.beneficiaryID}
                                    firstname={beneficiaryData.firstname}
                                    lastname={beneficiaryData.lastname}
                                    relationship={ beneficiaryData.relationship }
                                    beneficiaryType={beneficiaryData.type}
                                    percentage={beneficiaryData.shares}
                                    dateAdded={beneficiaryData.dateAdded} 
                                    parentID={beneficiaryData.parentID}
                                    type="beneficiary"
                                    edit={() => showEditBeneficiary(beneficiaryData, false)}
                                    remove={() => showRemoveBeneficiary(beneficiaryData, false)}
                                    iconCustomStyle={styles.iconStyle}
                                />
                            ))
                    }
                </div>

                <div>
                    <div style={{ ...styles.titleBlock, marginTop: 30 }} > 
                        <Text mediumLarge>{ i18n.t(tokens.titles["contingent beneficiaries"])}</Text>

                        <Button
                            testid="add-beneficiaries"
                            onClick={ () => addContingentBeneficiary(true) }
                            transparent
                            customStyle={{ marginLeft: 'auto' }}
                        >
                            <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                            { i18n.t(tokens.buttons["add contingent beneficiary"])}
                        </Button>
                    </div>

                    {
                        contingentBeneficiaries.length === 0 ?
                            <Text testid="beneficiaries" light medium>{ i18n.t(tokens.labels.beneficiaries.youHaveNotDefinedAnyContingentBeneficiaries) }</Text>
                            :
                            contingentBeneficiaries.map((beneficiaryData, i) => (
                                <PersonBlock
                                    testid={ i === 0 ? "contingent" : undefined }
                                    key={beneficiaryData.beneficiaryID}
                                    firstname={beneficiaryData.firstname}
                                    lastname={beneficiaryData.lastname}
                                    relationship={beneficiaryData.relationship}
                                    beneficiaryType={beneficiaryData.type}
                                    percentage={beneficiaryData.shares}
                                    dateAdded={beneficiaryData.dateAdded}
                                    parentID={beneficiaryData.parentID}
                                    type="beneficiary"
                                    edit={() => showEditBeneficiary(beneficiaryData, true)}
                                    remove={() => showRemoveBeneficiary(beneficiaryData, true)}
                                    iconCustomStyle={styles.iconStyle}
                                />
                            ))
                    }
                </div>

                <BeneficiaryModal
                    open={ beneficiaryModalOpen }
                    loader={loader}
                    isContingent={ isContingent }
                    beneficiaryData={ beneficiary }
                    onClose={ onClose }
                    onSave={ saveBeneficiary }
                    isEdit={ isEdit && beneficiary }
                    primaryBeneficiaries={primaryBeneficiaries}
                    contingentBeneficiaries={contingentBeneficiaries}
                    trustee={trustee}
                    editBeneficiary={ (...params) => dispatch(editBeneficiary(...params)) }
                    addBeneficiary={ (...params) => dispatch(addBeneficiary(...params)) }
                    openChangeRequestForm={ (...params) => openChangeRequestModal(...params) }
                    whitelabel={whitelabel}
                />

                <RemoveBeneficiaryModal
                    open={removeBeneficiaryModalOpen}
                    onClose={ onClose }
                    loader={loader}
                    isContingent={ isContingent }
                    removeBeneficiary={ (...params) => dispatch(removeBeneficiary(...params)) }
                    primaryBeneficiaries={primaryBeneficiaries}
                    contingentBeneficiaries={contingentBeneficiaries}
                    openChangeRequestForm={ (...params) => openChangeRequestModal(...params) }
                    beneficiaryData={ beneficiary }
                />

                <ChangeRequestModal
                    open={ changeRequestModalOpen }
                    onClose={ closeChangeRequestModal }
                    createChangeRequest={(...params) => dispatch(createChangeRequest(...params))}
                    deleteChangeRequest={(...params) => dispatch(deleteChangeRequest(...params))}
                    submitChangeRequest={(...params) => dispatch(submitChangeRequest(...params))}
                    changeRequestData={changeRequestData}
                    loader={loader}

                    // Files Props
                    type="users"
                    id={ meta.userID }
                    dispatch={ dispatch }
                    listFiles={ listFiles }
                    createFolder={ createFolder }
                    uploadFile={ uploadFile }
                    downloadFile={ downloadFile }
                    removeFile={ removeFile }
                />
            </ContentSmall>
        </Container>
    );
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    meta: state.user.meta,
    primaryBeneficiaries: state.user.beneficiaries.primary,
    contingentBeneficiaries: state.user.beneficiaries.contingent,
    trustee: state.user.trustee,
    whitelabel: state.whitelabel
});

export default (connect(mapStateToProps)(Beneficiaries));